import { palette, shades } from "lib/miolo";
import TextTooltip from "components/shared/TextTooltip";
import formatMoneyValue from "components/utils/formatMoneyValue";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { useState } from "react";
import tinycolor from "tinycolor2";
import { orderBy } from "lodash";

interface IEquityInterestsListProps {
  equityInterests: IBrazilianCompanyIssuerTopLevelFragment["equityInterests"];
}

export default function EquityInterestsList({ equityInterests }: IEquityInterestsListProps) {
  const [showDetailsForIndex, setShowDetailsForIndex] = useState<number | null>(null);

  return (
    <div
      css={{
        padding: 8,
        overflow: "hidden",
        backgroundColor: palette.white,
        borderRadius: 4,
      }}
    >
      <table
        css={{
          minWidth: "100%",
          fontSize: 14,
          fontFamily: "museo-sans-condensed",
          lineHeight: 1.33,
          letterSpacing: 0.6,
          textAlign: "left",
          textIndent: 0,
          borderColor: "inherit",
          borderCollapse: "collapse",
          "& > thead": {
            backgroundColor: palette.white,
          },
          "& th": {
            padding: "12px 16px",
            color: shades.greys.variants[2],
            fontWeight: 600,
            textTransform: "uppercase",
          },
          "& tr > td": {
            padding: "12px 16px",
          },
          "& > tbody": {
            "& tr": {
              borderColor: palette.fiord,
              borderTopWidth: 1,
              "&:hover": {
                backgroundColor: tinycolor(palette.catskillWhite).lighten(2).toHexString(),
                "& td:first-of-type": {
                  borderTopLeftRadius: 6,
                  borderBottomLeftRadius: 6,
                },
                "& td:last-of-type": {
                  borderTopRightRadius: 6,
                  borderBottomRightRadius: 6,
                },
              },
            },
          },
          "& tbody > tr": {
            borderTop: `solid 1px ${tinycolor(palette.mystic).lighten(3).toHexString()}`,
            borderBottom: "none",
            "&:hover, &:hover + tr": {
              borderTop: "solid 1px transparent",
            },
          },
        }}
      >
        <thead>
          <tr>
            <th css={{ width: "70%" }}>Denominação Social</th>
            <th css={{ textAlign: "center" }}>Participação (%)</th>
          </tr>
        </thead>

        <tbody>
          {orderBy(equityInterests, ["participation"], ["desc"]).map((node, index) => {
            const isShowingDetails = showDetailsForIndex === index;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${node.name}/${index}`}>
                <td>
                  <div css={{ display: "grid", rowGap: 8 }}>
                    <strong css={{ fontSize: 16, color: shades.greys.variants[3] }}>{node.name}</strong>

                    <div>
                      <span css={{ lineHeight: 1.33, color: shades.greys.variants[4] }}>
                        Valor de Contábil: <strong>{formatMoneyValue(node.bookValue)}</strong>
                      </span>
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowDetailsForIndex(isShowingDetails ? null : index);
                        }}
                        css={{
                          margin: 0,
                          padding: 0,
                          color: palette.mariner,
                          fontSize: 14,
                          letterSpacing: 1,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          appearance: "none",
                        }}
                      >
                        {isShowingDetails ? "Esconder detalhes" : "Ver descrição das atividades"}
                      </button>
                    </div>
                  </div>

                  {isShowingDetails && (
                    <p
                      css={{
                        padding: 12,
                        color: shades.black[0],
                        fontWeight: 300,
                        fontSize: 16,
                        lineHeight: 1.33,
                        letterSpacing: 1,
                        backgroundColor: palette.catskillWhite,
                        borderRadius: 8,
                      }}
                    >
                      {node.descriptionOfActivitiesDeveloped}
                    </p>
                  )}
                </td>

                <td css={{ textAlign: "center" }}>
                  <span>{Math.ceil(node.participation)}%</span>
                </td>

                <td css={{ textAlign: "right" }}>
                  {node.geographicalCountry && (
                    <div>
                      <TextTooltip
                        key={node.geographicalCountry.isoCode}
                        id={`${node.name}/${node.geographicalCountry.isoCode}`}
                        placement="top"
                        content={node.geographicalCountry.name}
                      >
                        {({ accessibility, bind, ref }) => (
                          <img
                            {...accessibility}
                            {...bind}
                            ref={ref}
                            title={node.geographicalCountry?.name}
                            alt={node.geographicalCountry?.name}
                            src={node.geographicalCountry?.flagSvg.ovalHref}
                            width={24}
                            height={24}
                            css={{
                              width: 24,
                              height: 24,
                            }}
                          />
                        )}
                      </TextTooltip>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
